import React, { useRef, useState } from 'react';

import { DialogActions, Typography } from '@mui/material';
import { BellRing } from 'lucide-react';
import { AlineButton, AlineDialog } from 'src/components';
import { AlineAlert } from 'src/components/generic/AlineAlert';
import { makeStyles } from 'tss-react/mui';

import { useTaskComposerDraft } from './task-composer-draft';
import { TaskDialogTextarea } from './TaskDialogTextarea';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  notified: {
    alignItems: 'center',
    backgroundColor: theme.appColors.hoverBackgroundSubtle,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 8,
    color: theme.appColors.strongType,
    display: 'flex',
    fontSize: 14,
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
  },
  notifiedIcon: {
    color: theme.appColors.iconsType,
    flexShrink: 0,
  },
  paper: {
    width: 520,
  },
}));

interface TaskCommentDialogProps {
  label: string;
  onClose: () => void;
  /** Resolves to true when the action went through; the dialog stays open otherwise. */
  onSubmit: (comment: string) => Promise<boolean>;
  open: boolean;
  placeholder: string;
  primaryText: string;
  title: string;
  /** Extra fields between the message and the comment, such as who the action goes to. */
  children?: React.ReactNode;
  isCommentRequired?: boolean;
  isSubmitDisabled?: boolean;
  message?: string;
  /** "Sam and Dana will be notified.", shown as a callout above the comment. */
  notifiedText?: string | null;
  /** A consequence to confirm before submitting, shown as a warning at the top. */
  warning?: string | null;
}

/** Collects the comment, note or reason that goes with a review action. */
export function TaskCommentDialog({
  children,
  isCommentRequired = false,
  isSubmitDisabled = false,
  label,
  message,
  notifiedText,
  onClose,
  onSubmit,
  open,
  placeholder,
  primaryText,
  title,
  warning,
}: TaskCommentDialogProps): JSX.Element {
  const { classes } = useStyles();
  const isFromDraftRef = useRef(false);
  const [comment, setComment] = useState('');
  const composerDraft = useTaskComposerDraft();
  const [submitting, setSubmitting] = useState(false);
  const isBlocked = isSubmitDisabled || (isCommentRequired && !comment.trim());

  const handleEnter = () => {
    const draft = composerDraft?.getDraft() ?? '';
    isFromDraftRef.current = Boolean(draft);
    setComment(draft);
  };

  const handleClose = () => {
    if (submitting) return;
    setComment('');
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const succeeded = await onSubmit(comment.trim());
      if (succeeded) {
        // The draft went out with the action, so it leaves the composer too.
        if (isFromDraftRef.current) composerDraft?.clear();
        setComment('');
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlineDialog
      TransitionProps={{ onEnter: handleEnter }}
      classes={{ paper: classes.paper }}
      open={open}
      title={title}
      onClose={handleClose}
    >
      <div className={classes.body}>
        {Boolean(warning) && <AlineAlert severity="warning">{warning}</AlineAlert>}
        {Boolean(message) && <Typography variant="body1">{message}</Typography>}
        {Boolean(notifiedText) && (
          <div className={classes.notified}>
            <BellRing className={classes.notifiedIcon} size={16} />
            <span>{notifiedText}</span>
          </div>
        )}
        {children}
        <TaskDialogTextarea
          autoFocus
          label={label}
          placeholder={placeholder}
          required={isCommentRequired}
          value={comment}
          onChange={setComment}
        />
      </div>
      <DialogActions>
        <AlineButton color="primary" disabled={submitting} variant="outlined" onClick={handleClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={isBlocked}
          loading={submitting}
          variant="contained"
          onClick={() => void handleSubmit()}
        >
          {primaryText}
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}
